import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {organizationTypes} from '@packages/domains/aeo/data-types';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'create-brand-context' as const;

const addressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

const argumentZod = z.object({
  name: z.string().describe('Brand name (required).'),
  description: z.string().optional().describe('Brand description.'),
  aliases: z
    .array(z.string())
    .optional()
    .describe('Alternative names for the brand.'),
  contact: z
    .object({
      email: z.string().optional(),
      telephone: z.string().optional(),
    })
    .optional()
    .describe('Contact information.'),
  about: z
    .object({
      foundingDate: z.string().optional().describe('ISO date string.'),
      organizationType: z.enum(organizationTypes).optional(),
      industry: z.string().optional(),
      description: z.string().optional(),
    })
    .optional()
    .describe('About the organization.'),
  locations: z
    .array(
      z.object({
        name: z.string(),
        isHeadquarters: z.boolean().optional(),
        address: addressSchema,
      })
    )
    .optional()
    .describe('Office locations.'),
  people: z
    .array(
      z.object({
        name: z.string(),
        role: z.string().optional(),
      })
    )
    .optional()
    .describe('Key people.'),
  competitors: z
    .array(
      z.object({
        name: z.string(),
        aliases: z.array(z.string()).optional(),
        domains: z.array(z.string()).optional(),
      })
    )
    .optional()
    .describe('Competitors with their names, aliases, and domains.'),
});

export const createBrandContextManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create brand context',
  family: 'brand',
  llmTool: {
    name: toolName,
    description:
      'Creates a new brand for the current site (from MCP session context). Only one brand per site is allowed in Alpha. Includes optional contact info, about, locations, people, and competitors.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.AEO_MEASUREMENT,
  confirmBeforeExecuting: true,
});

export type CreateBrandContextArgumentType = z.infer<typeof argumentZod>;
